import { MaatregelDto } from "@/shared/types";
import { uuid } from "@/shared/utils";
import { z } from "zod";

class MaatregelTransformationError extends Error {
    private constructor(message: string) {
        super(message);
    }

    public static missingVanOrNaarStationCode(van: string | undefined, naar: string | undefined) {
        return new MaatregelTransformationError(
            `One (or both) required fields of 'vanStationCode' or 'naarStationCode' was not defined: van=${van}, naar=${naar}`
        );
    }

    public static missingVanStationCode() {
        return new MaatregelTransformationError(`Required field 'vanStationCode' missing`);
    }

    public static missingOmleiding() {
        return new MaatregelTransformationError(`Required field 'omleiding' missing`);
    }

    public static incorrectActiesoort(actiesoort: string | undefined, expected: string[]) {
        return new MaatregelTransformationError(
            `Expected field 'actie' to have value in: [${expected.join(", ")}], but was: '${actiesoort}'`
        );
    }

    public static rijdtGroepIsFrontendOnly() {
        return new MaatregelTransformationError(
            "Tried to transform a RijdtGroepMaatregel to a MaatregelDto, but this should never happen, since RijdtGroep maatregelen should only exist in the frontend"
        );
    }
}

type Omleiding = {
    extraReisTijd?: string;
    toelichting?: string;
    viaStationCodes: string[];
};

/* The backend does not send handmatigBewerkt yet, so an unknown value means the maatregel was never edited by hand. */
const statusFromDto = (dto: MaatregelDto): BaseMaatregel["status"] => (dto.handmatigBewerkt ? "Bewerkt" : "Bestaand");

type BaseMaatregel = {
    id: string;
    treinserie: string;
    type: "Rijdt" | "RijdtGroep" | "RijdtNiet" | "StopVerwijderen" | "ExtraStop" | "Omleiding";
    richting: "ViceVersa" | "Heen";
    vanStationCode?: string;
    naarStationCode?: string;
    opmerking?: string;
    vertrekTijd?: string;
    handmatigToegevoegd: boolean;
    status: "Nieuw" | "In Bewerking" | "Bewerkt" | "Bestaand";
    /* Frontend-only: the opmerking comes from Spoorweb and has not been accepted (yet) as toelichting. */
    opmerkingIsSpoorwebSuggestie: boolean;
    /* Frontend-only: a toelichting from the huidige VSM that is offered as a suggestion in the nieuwe versie. */
    huidigeToelichting?: string;
};

export type RijdtGroepMaatregel = BaseMaatregel & {
    type: "RijdtGroep";
    maatregelen: RijdtMaatregel[];
};

// This is a maatregel that only exists in the FE, acting as a single maatregel, but representing multiple (almost-empty) maatregelen
export const RijdtGroepMaatregel = {
    fromMaatregelen: (maatregelen: RijdtMaatregel[]): RijdtGroepMaatregel => ({
        id: uuid(),
        treinserie: maatregelen.map((m) => m.treinserie).join(", "),
        richting: "ViceVersa",
        type: "RijdtGroep",
        maatregelen: maatregelen,
        handmatigToegevoegd: false,
        status: "Bestaand",
        opmerkingIsSpoorwebSuggestie: false,
    }),
};

export type RijdtMaatregel = BaseMaatregel & {
    type: "Rijdt";
};

export const RijdtMaatregel = {
    createEmpty: (): RijdtMaatregel => ({
        id: uuid(),
        type: "Rijdt",
        treinserie: "",
        richting: "ViceVersa",
        vanStationCode: "",
        naarStationCode: "",
        handmatigToegevoegd: true,
        status: "In Bewerking",
        opmerkingIsSpoorwebSuggestie: false,
    }),
    fromDto: (dto: MaatregelDto, isSpoorwebSuggestie = false) => {
        const domain = {
            ...dto,
            type: "Rijdt",
            status: statusFromDto(dto),
            opmerkingIsSpoorwebSuggestie: isSpoorwebSuggestie && !!dto.opmerking,
        } satisfies RijdtMaatregel;

        if (Maatregel.rijdtMaarZonderExtraInfo(domain)) {
            // explicitly set richting to "ViceVersa", as sometimes these RijdtMaatregelen that do not have any extra info
            // had the richting: "Heen", causing strange issues when editing these.
            return {
                ...domain,
                richting: "ViceVersa",
            } satisfies RijdtMaatregel;
        }
        return domain;
    },
};

export type RijdtNietMaatregel = BaseMaatregel & {
    type: "RijdtNiet";
    vanStationCode: string;
    naarStationCode: string;
};

export const RijdtNietMaatregel = {
    createEmpty: (): RijdtNietMaatregel => ({
        id: uuid(),
        type: "RijdtNiet",
        treinserie: "",
        richting: "ViceVersa",
        vanStationCode: "",
        naarStationCode: "",
        handmatigToegevoegd: true,
        status: "In Bewerking",
        opmerkingIsSpoorwebSuggestie: false,
    }),
    fromDto: (dto: MaatregelDto, isSpoorwebSuggestie = false) => {
        if (!dto.vanStationCode || !dto.naarStationCode) {
            throw MaatregelTransformationError.missingVanOrNaarStationCode(dto.vanStationCode, dto.naarStationCode);
        }

        return {
            ...dto,
            vanStationCode: dto.vanStationCode,
            naarStationCode: dto.naarStationCode,
            type: "RijdtNiet",
            status: statusFromDto(dto),
            opmerkingIsSpoorwebSuggestie: isSpoorwebSuggestie && !!dto.opmerking,
        } satisfies RijdtNietMaatregel;
    },
};

export type StopMaatregel = BaseMaatregel & {
    type: "StopVerwijderen" | "ExtraStop";
    vanStationCode: string;
};

export const StopMaatregel = {
    actiesoorten: ["StopVerwijderen", "ExtraStop", "Keren"],
    isCorrectType(actie: MaatregelDto["actie"]): actie is "StopVerwijderen" | "ExtraStop" {
        return this.actiesoorten.includes(actie);
    },
    fromDto(dto: MaatregelDto, isSpoorwebSuggestie = false): StopMaatregel {
        if (!dto.vanStationCode) {
            throw MaatregelTransformationError.missingVanStationCode();
        }

        if (!this.isCorrectType(dto.actie)) {
            throw MaatregelTransformationError.incorrectActiesoort(dto.actie, this.actiesoorten);
        }

        return {
            ...dto,
            vanStationCode: dto.vanStationCode,
            type: dto.actie,
            status: statusFromDto(dto),
            opmerkingIsSpoorwebSuggestie: isSpoorwebSuggestie && !!dto.opmerking,
        } satisfies StopMaatregel;
    },
};

export type OmleidingMaatregel = BaseMaatregel & {
    type: "Omleiding";
    omleiding: Omleiding;
};

export const OmleidingMaatregel = {
    createEmpty: (): OmleidingMaatregel => ({
        id: uuid(),
        type: "Omleiding",
        treinserie: "",
        richting: "Heen",
        omleiding: {
            viaStationCodes: [],
        },
        handmatigToegevoegd: true,
        status: "In Bewerking",
        opmerkingIsSpoorwebSuggestie: false,
    }),
    fromDto(dto: MaatregelDto, isSpoorwebSuggestie = false): OmleidingMaatregel {
        if (!dto.omleiding) {
            throw MaatregelTransformationError.missingOmleiding();
        }

        return {
            ...dto,
            omleiding: dto.omleiding,
            type: "Omleiding",
            status: statusFromDto(dto),
            opmerkingIsSpoorwebSuggestie: isSpoorwebSuggestie && !!dto.opmerking,
        };
    },
};

export type Maatregel = RijdtMaatregel | RijdtGroepMaatregel | RijdtNietMaatregel | StopMaatregel | OmleidingMaatregel;
export type MaatregelType = Maatregel["type"];

type ReturnTypeMaatregel<T extends "Rijdt" | "RijdtNiet" | "Aanvullend"> = T extends "Rijdt"
    ? RijdtMaatregel | RijdtGroepMaatregel
    : T extends "RijdtNiet"
      ? RijdtNietMaatregel
      : StopMaatregel | OmleidingMaatregel;

export const Maatregel = {
    isInBewerking: (maatregel: Maatregel): boolean => maatregel.status === "In Bewerking",
    editable: (maatregel: Maatregel): Maatregel => ({
        ...maatregel,
        status: "In Bewerking",
    }),
    /* Accept the visible suggestion: the toelichting of the huidige versie takes precedence over the Spoorweb one. */
    acceptToelichting: (maatregel: Maatregel): Maatregel =>
        maatregel.huidigeToelichting
            ? {
                  ...maatregel,
                  opmerking: maatregel.huidigeToelichting,
                  opmerkingIsSpoorwebSuggestie: false,
                  huidigeToelichting: undefined,
              }
            : { ...maatregel, opmerkingIsSpoorwebSuggestie: false },
    /* Remove the visible suggestion. Ignoring the huidige toelichting reveals the Spoorweb suggestion, if there is one. */
    removeToelichting: (maatregel: Maatregel): Maatregel =>
        maatregel.huidigeToelichting
            ? { ...maatregel, huidigeToelichting: undefined }
            : { ...maatregel, opmerking: undefined, opmerkingIsSpoorwebSuggestie: false },
    /* Which suggestion is currently offered, if any. The huidige toelichting is shown before the Spoorweb one. */
    pendingToelichtingSuggestie: (maatregel: Maatregel): "huidig" | "spoorweb" | undefined => {
        if (maatregel.huidigeToelichting) {
            return "huidig";
        }
        return maatregel.opmerkingIsSpoorwebSuggestie && Maatregel.hasToelichting(maatregel) ? "spoorweb" : undefined;
    },
    /* Whether the maatregel has a non-blank toelichting (accepted or still a Spoorweb suggestion). */
    hasToelichting: (maatregel: Maatregel): boolean => !!maatregel.opmerking?.trim(),
    /* Whether there is anything to render on the toelichting row: an actual toelichting or an open suggestion. */
    hasToelichtingOfSuggestie: (maatregel: Maatregel): boolean => Maatregel.hasToelichting(maatregel) || !!maatregel.huidigeToelichting,
    /* The section a maatregel is listed under, used to pair maatregelen of the huidige and nieuwe versie. */
    getSection: (maatregel: Maatregel): "Rijdt" | "RijdtNiet" | "Aanvullend" => {
        switch (maatregel.type) {
            case "Rijdt":
            case "RijdtGroep":
                return "Rijdt";
            case "RijdtNiet":
                return "RijdtNiet";
            default:
                return "Aanvullend";
        }
    },
    /* Whether both maatregelen describe the same traject. A→B and B→A are different trajecten. */
    hasSameTraject: (a: Maatregel, b: Maatregel): boolean =>
        (a.vanStationCode ?? "") === (b.vanStationCode ?? "") && (a.naarStationCode ?? "") === (b.naarStationCode ?? ""),
    /* Offer the toelichting of the huidige versie as a suggestion on the matching maatregel of the nieuwe versie. */
    withHuidigeToelichting: (nieuweMaatregelen: Maatregel[], huidigeMaatregelen: Maatregel[]): Maatregel[] =>
        nieuweMaatregelen.map((maatregel) => {
            const huidig = huidigeMaatregelen.find(
                (h) =>
                    h.treinserie === maatregel.treinserie &&
                    Maatregel.getSection(h) === Maatregel.getSection(maatregel) &&
                    Maatregel.hasSameTraject(h, maatregel) &&
                    Maatregel.hasToelichting(h)
            );

            if (!huidig) {
                return maatregel;
            }
            return { ...maatregel, huidigeToelichting: huidig.opmerking };
        }),
    revert: (maatregel: Maatregel): Maatregel => ({
        ...maatregel,
        status: "Bestaand",
    }),
    preserveMetadataForUpdate: (maatregel: Maatregel, id: string, handmatigToegevoegd: boolean): Maatregel => ({
        ...maatregel,
        // Preserve the original ID when editing so we can update
        id: id,
        // Update the status to bewerkt when editing is finished (transition from "In Bewerking" to "Bewerkt")
        status: "Bewerkt",
        // Preserve if a "Nieuw"/handmatigToegevoegd=true maatregel was edited, otherwise false
        handmatigToegevoegd: handmatigToegevoegd ?? false,
    }),
    createEmpty: (type: "Rijdt" | "RijdtNiet" | "Aanvullend"): Maatregel => {
        switch (type) {
            case "Rijdt":
                return RijdtMaatregel.createEmpty();
            case "RijdtNiet":
                return RijdtNietMaatregel.createEmpty();
            case "Aanvullend":
                return OmleidingMaatregel.createEmpty();
        }
    },
    withRijdtGroep: (
        maatregelen: Maatregel[],
        includeInRijdtGroep?: (maatregel: Maatregel) => maatregel is RijdtMaatregel
    ): Maatregel[] => {
        const predicate = includeInRijdtGroep ?? Maatregel.rijdtMaarZonderExtraInfo;
        const existingRijdtGroep = maatregelen.find((m) => m.type === "RijdtGroep");
        const zonderRijdtGroep = maatregelen.filter((m) => m.type !== "RijdtGroep");
        const maatregelenVoorRijdtGroep = zonderRijdtGroep.filter(predicate).sort((a, b) => a.treinserie.localeCompare(b.treinserie));

        if (maatregelenVoorRijdtGroep.length === 0) {
            return zonderRijdtGroep.sort(Maatregel.sortByTreinserieAndType);
        }

        const rijdtGroepMaatregel: RijdtGroepMaatregel = {
            ...RijdtGroepMaatregel.fromMaatregelen(maatregelenVoorRijdtGroep),
            // Preserve id and status of the maatregel
            ...(existingRijdtGroep && { id: existingRijdtGroep.id, status: existingRijdtGroep.status }),
        };
        return [...zonderRijdtGroep, rijdtGroepMaatregel].sort(Maatregel.sortByTreinserieAndType);
    },

    // withRijdtGroep only appends the groep row, filterBy hides the maatregelen it absorbed.
    // Both sides need the same predicate, otherwise a treinserie shows up twice or not at all.
    sectionWithRijdtGroep<T extends "Rijdt" | "RijdtNiet" | "Aanvullend">(
        maatregelen: Maatregel[],
        type: T,
        inRijdtGroep?: (maatregel: Maatregel) => maatregel is RijdtMaatregel
    ): ReturnTypeMaatregel<T>[] {
        return Maatregel.withRijdtGroep(maatregelen, inRijdtGroep).filter(Maatregel.filterBy(type, inRijdtGroep));
    },

    filterBy<T extends "Rijdt" | "RijdtNiet" | "Aanvullend">(type: T, excludeFromRijdt?: (maatregel: Maatregel) => boolean) {
        return (maatregel: Maatregel): maatregel is ReturnTypeMaatregel<T> => {
            if (type === "Aanvullend") {
                return maatregel.type === "ExtraStop" || maatregel.type === "StopVerwijderen" || maatregel.type === "Omleiding";
            }
            if (type === "Rijdt") {
                // By default, items that are also part of a RijdtGroep are excluded from the "Rijdt" filter.
                // However, which items are considered part of a RijdtGroep can vary depending on the context (e.g., actie dialog vs vsm-summary).
                // To support this, you can override the default exclusion logic by passing a custom excludeFromRijdt function.
                const shouldExclude = excludeFromRijdt
                    ? excludeFromRijdt(maatregel)
                    : Maatregel.rijdtMaarZonderExtraInfo(maatregel) && maatregel.status !== "In Bewerking";

                if (shouldExclude) {
                    return false;
                }
                return maatregel.type === "Rijdt" || maatregel.type === "RijdtGroep";
            }
            return maatregel.type === type;
        };
    },

    // Handmatig toegevoegde en bewerkte maatregelen horen ook in de RijdtGroep, zolang ze verder geen extra info hebben.
    rijdtMaarZonderExtraInfo: (maatregel: Maatregel): maatregel is RijdtMaatregel => {
        const hasNoExtraInfo = Maatregel.rijdtMaarGeenVanEnNaar(maatregel) && !Maatregel.hasToelichtingOfSuggestie(maatregel);
        return maatregel.type === "Rijdt" && hasNoExtraInfo && maatregel.status !== "In Bewerking";
    },

    rijdtMaarGeenVanEnNaar: (maatregel: Maatregel): maatregel is RijdtMaatregel => {
        const geenVanEnNaar = !maatregel.vanStationCode && !maatregel.naarStationCode;
        return maatregel.type === "Rijdt" && geenVanEnNaar;
    },

    sortByTreinserieAndType: (a: Maatregel, b: Maatregel): number => {
        // Always sort "RijdtGroep" types last, otherwise sort by treinserie
        if (a.type === "RijdtGroep") {
            return 1;
        }
        if (b.type === "RijdtGroep") {
            return -1;
        }
        return parseInt(a.treinserie) - parseInt(b.treinserie);
    },
    typeToActie: (type: MaatregelType): "BlijftRijden" | "Opheffen" | "StopVerwijderen" | "ExtraStop" | "Omleiding" => {
        switch (type) {
            case "Rijdt":
                return "BlijftRijden";
            case "RijdtNiet":
                return "Opheffen";
            case "RijdtGroep":
                throw MaatregelTransformationError.rijdtGroepIsFrontendOnly();
            default:
                return type;
        }
    },
    fromDto: (dto: MaatregelDto, isSpoorwebSuggestie = false): Maatregel => {
        switch (dto.actie) {
            case "BlijftRijden":
                return RijdtMaatregel.fromDto(dto, isSpoorwebSuggestie);
            case "Opheffen":
                return RijdtNietMaatregel.fromDto(dto, isSpoorwebSuggestie);
            case "ExtraStop":
            case "StopVerwijderen":
            case "Keren":
                return StopMaatregel.fromDto(dto, isSpoorwebSuggestie);
            case "Omleiding":
                return OmleidingMaatregel.fromDto(dto, isSpoorwebSuggestie);
        }
    },
    toDto: (maatregel: Maatregel): MaatregelDto => ({
        id: maatregel.id,
        treinserie: maatregel.treinserie,
        richting: maatregel.richting,
        vanStationCode: maatregel.vanStationCode,
        naarStationCode: maatregel.naarStationCode,
        // an unaccepted Spoorweb suggestion is not (yet) a toelichting of this maatregel, so it is not persisted
        opmerking: maatregel.opmerkingIsSpoorwebSuggestie ? undefined : maatregel.opmerking,
        vertrekTijd: maatregel.vertrekTijd,
        actie: Maatregel.typeToActie(maatregel.type),
        omleiding: maatregel.type === "Omleiding" ? maatregel.omleiding : undefined,
        handmatigToegevoegd: maatregel.handmatigToegevoegd,
        handmatigBewerkt: maatregel.status === "Bewerkt",
    }),
};

type Digit = "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9";
type MinuteTens = "0" | "1" | "2" | "3" | "4" | "5";
type Minutes = `${MinuteTens}${Digit}` | undefined;
type VertrekTijd = `:${Minutes}` | undefined;

const vertrekTijdRegex = /^:?(([0-5]\d)(?:\/([0-5]\d))?)$/;
export const VertrekTijd = {
    zodType: z.string().regex(vertrekTijdRegex, { error: "00-59" }).optional(),
    toMinutes(tijd: string | undefined): Minutes {
        if (!tijd || !vertrekTijdRegex.test(tijd)) {
            return undefined;
        }
        const match = vertrekTijdRegex.exec(tijd);
        return match ? (match[1] as Minutes) : undefined;
    },
    toVertrekTijd(minutes: string | undefined): VertrekTijd {
        if (!minutes) {
            return undefined;
        }
        const match = vertrekTijdRegex.exec(minutes);
        return (match ? `:${match[1].padStart(2, "0")}` : undefined) as VertrekTijd;
    },
};
